/**
 * A Word library entry open in the in-app docx editor: the document the
 * agents copy and edit, shown as a document rather than as its PDF rendition.
 * It opens in Viewing mode; an editor of the library can switch to Editing,
 * and saving writes the docx back over the entry's file (`saveEntryDocx`), so
 * an entry converted from a PDF keeps its original PDF and its conversion record.
 * Mirrors `CaseFileDocxEditor`, minus the case-only actions.
 */
import {
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { BrandLoader } from "@/components/brand";

import type { DraftDocxEditorHandle } from "../../draftEditor/DraftDocxEditor";
import {
  confirmDiscardDocuments,
  useUnsavedDocument
} from "../../draftEditor/documentState";
import type { DocxEditorMode } from "../../draftEditor/surface";
import type { FirmLibraryEntry } from "../types";

// The editor is a large vendor bundle; only people who open a Word entry pay for it.
const DraftDocxEditor = lazy(() =>
  import("../../draftEditor/DraftDocxEditor").then((m) => ({
    default: m.DraftDocxEditor
  }))
);

type Props = {
  entry: FirmLibraryEntry;
  scope: FirmScope;
  /** Library editors may switch to Editing and save; everyone else views. */
  canEdit: boolean;
  /** The entry as the server returned it after a save. */
  onSaved: (entry: FirmLibraryEntry) => void;
};

export const libraryRelayId = (entryId: string) => `library:${entryId}`;

/**
 * Ask before closing or leaving an entry whose document has unsaved edits.
 * `true` when there is nothing to lose or the editor agreed to discard.
 */
export const confirmLeaveLibraryEntry = (entryId: string) =>
  confirmDiscardDocuments(libraryRelayId(entryId));

const filenameOf = (entry: FirmLibraryEntry) =>
  entry.original_filename?.trim() || `${entry.name || "document"}.docx`;

/** What the drawer can ask of the open document. */
export type LibraryDocxViewerHandle = {
  /** Select and scroll to the first place `text` appears; false if absent or not loaded yet. */
  reveal: (text: string) => boolean;
  /** Whether the document has edits that are not saved yet. */
  isDirty: () => boolean;
};

export const LibraryDocxViewer = forwardRef<LibraryDocxViewerHandle, Props>(
  function LibraryDocxViewer({ entry, scope, canEdit, onSaved }, ref) {
    const [mode, setMode] = useState<DocxEditorMode>("viewing");
    const editorRef = useRef<DraftDocxEditorHandle | null>(null);
    const dirty = useRef(false);
    const isDirty = useCallback(() => dirty.current, []);
    useImperativeHandle(
      ref,
      () => ({
        reveal: (text) =>
          editorRef.current?.surface()?.revealText(text) ?? false,
        isDirty
      }),
      [isDirty]
    );
    // The editor keeps these callbacks for its lifetime; read the latest props
    // through refs so a poll that refreshes the row does not reload the document.
    const entryRef = useRef(entry);
    entryRef.current = entry;
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;

    const onDirtyChange = useCallback((next: boolean) => {
      dirty.current = next;
    }, []);
    const discard = useCallback(() => editorRef.current?.discardRecovery(), []);
    useUnsavedDocument(
      libraryRelayId(entry.id),
      entry.name || filenameOf(entry),
      isDirty,
      discard
    );

    const loadDocx = useCallback(async () => {
      const { bytes, checksum } = await firmKnowledgeApi.getEntryDocx(
        entryRef.current.id,
        scopeRef.current
      );
      return {
        bytes,
        filename: filenameOf(entryRef.current),
        unresolvedCitations: 0,
        updatedAt: checksum
      };
    }, []);

    const saveDocx = useCallback(
      async (bytes: ArrayBuffer, options: { ifMatch?: string | null }) => {
        const { entry: updated, checksum } =
          await firmKnowledgeApi.saveEntryDocx(
            entryRef.current.id,
            bytes,
            options.ifMatch ?? null,
            scopeRef.current
          );
        onSavedRef.current(updated);
        return { updatedAt: checksum };
      },
      []
    );

    const relayId = libraryRelayId(entry.id);
    return (
      <div className="h-full min-h-0" data-testid="library-docx-viewer">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <BrandLoader label="Loading the Word viewer" />
            </div>
          }
        >
          <DraftDocxEditor
            ref={editorRef}
            key={`${entry.id}:${entry.mime_type}`}
            draftId={relayId}
            recoveryKey={relayId}
            loadDocx={loadDocx}
            saveDocx={canEdit ? saveDocx : undefined}
            readOnly={!canEdit}
            mode={mode}
            onModeChange={setMode}
            onDirtyChange={onDirtyChange}
            title={entry.name || filenameOf(entry)}
          />
        </Suspense>
      </div>
    );
  }
);
